import React, { useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout/Layout';
import { api } from '../../services/api';
import { getCategories, autoCategorize } from '../../data/categoriesStore';
import styles from './ProduitDetail.module.css';
import fStyles from './ProduitCreate.module.css';

interface FournisseurProduct {
  name: string;
  image: string;
  imageLarge: string;
  url: string;
  price: string;
  description: string;
}

// Produit sélectionné en mode auto, avec ses paramètres propres
interface SelectedProduct {
  _key: number;
  fournisseur: FournisseurProduct;
  // paramètres éditables individuellement si besoin
  name: string;
  slug: string;
}

type PriceSyncMode = 'manual' | 'product' | 'discount';

interface LocalVariant {
  _tempId: number;
  name: string;
  price: string;
  stock: number;
  sku: string;
  option1Value: string;
  option2Value: string;
  option3Value: string;
  priceSyncMode: PriceSyncMode;
}

const emptyVariant = (tempId: number): LocalVariant => ({
  _tempId: tempId,
  name: '',
  price: '0',
  stock: 0,
  sku: '',
  option1Value: '',
  option2Value: '',
  option3Value: '',
  priceSyncMode: 'product',
});

let tempIdCounter = 1;
let selectedKeyCounter = 1;

const toSlug = (str: string) =>
  str.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const parsePrice = (val: string): number => parseFloat(val.replace(',', '.')) || 0;

// ─── MODE MANUEL ─────────────────────────────────────────────────────────────
const ModeManuel: React.FC<{ navigate: ReturnType<typeof useNavigate> }> = ({ navigate }) => {
  const categoryList = useMemo(() => getCategories(), []);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [price, setPrice] = useState('');
  const [discountPrice, setDiscountPrice] = useState('');
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [stock, setStock] = useState('0');
  const [description, setDescription] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [prixAchat, setPrixAchat] = useState('');
  const [supplierSku, setSupplierSku] = useState('');
  const [bestSeller, setBestSeller] = useState(false);
  const [bonneAffaire, setBonneAffaire] = useState(false);
  const [masquer, setMasquer] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageExtension, setImageExtension] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [variants, setVariants] = useState<LocalVariant[]>([]);
  const [addingVariant, setAddingVariant] = useState(false);
  const [newVariant, setNewVariant] = useState<LocalVariant | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Fournisseur (mode manuel garde sa propre recherche pour 1 produit)
  const [fournisseurQuery, setFournisseurQuery] = useState('');
  const [fournisseurResults, setFournisseurResults] = useState<FournisseurProduct[]>([]);
  const [fournisseurLoading, setFournisseurLoading] = useState(false);
  const [fournisseurError, setFournisseurError] = useState('');
  const [showFournisseur, setShowFournisseur] = useState(false);

  const subCategoryList = useMemo(
    () => categoryList.find((c) => c.name === category)?.subCategories ?? [],
    [categoryList, category]
  );
  const priceNum = parsePrice(price);
  const discountPriceNum = discountPrice.trim() !== '' ? parsePrice(discountPrice) : null;

  const handleNameChange = (val: string) => {
    setName(val);
    setSlug(toSlug(val));
    const result = autoCategorize(val);
    if (result && !category) { setCategory(result.category); setSubCategory(result.subCategory); }
  };

  const getEffectivePrice = (v: LocalVariant) => {
    if (v.priceSyncMode === 'product') return priceNum;
    if (v.priceSyncMode === 'discount') return discountPriceNum ?? priceNum;
    return parsePrice(v.price);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setPreviewUrl(URL.createObjectURL(file)); setUploading(true); setUploadStatus(''); setUploadError('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post('upload/product-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImageUrl(res.data.url); setImageExtension(res.data.extension); setUploadStatus(`✓ ${res.data.fileName}`);
    } catch (err: any) { setUploadError('Erreur upload : ' + (err.response?.data?.message || err.message)); setPreviewUrl(''); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const handleDeleteImage = async () => {
    const fn = imageUrl.split('/').pop() ?? '';
    if (fn) { try { await api.delete(`upload/product-image?fileName=${encodeURIComponent(fn)}`); } catch { } }
    setImageUrl(''); setImageExtension(''); setPreviewUrl(''); setUploadStatus('');
  };

  const handleFournisseurSearch = async () => {
    if (fournisseurQuery.trim().length < 2) return;
    setFournisseurLoading(true); setFournisseurError(''); setFournisseurResults([]);
    try {
      const res = await api.get(`fournisseur/search?q=${encodeURIComponent(fournisseurQuery.trim())}`);
      setFournisseurResults(res.data.data ?? []);
      if ((res.data.data ?? []).length === 0) setFournisseurError('Aucun produit trouvé.');
    } catch (err: any) { setFournisseurError('Erreur : ' + (err.response?.data?.message || err.message)); }
    finally { setFournisseurLoading(false); }
  };

  const handleImportFournisseur = async (p: FournisseurProduct) => {
    handleNameChange(p.name);
    const imgUrl = p.imageLarge || p.image;
    if (imgUrl) { setImageUrl(imgUrl); setImageExtension(imgUrl.split('.').pop()?.toLowerCase().split('?')[0] ?? ''); setPreviewUrl(imgUrl); setUploadStatus(''); }
    setShowFournisseur(false);
    // Récupérer description + tableau depuis la page détail fournisseur
    if (p.url) {
      try {
        const res = await api.get(`fournisseur/detail?url=${encodeURIComponent(p.url)}`);
        if (res.data?.description) setDescription(res.data.description);
        else if (p.description) setDescription(p.description);
      } catch {
        if (p.description) setDescription(p.description);
      }
    } else if (p.description) {
      setDescription(p.description);
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) { alert('Le nom est requis.'); return; }
    if (uploading) { alert("Upload en cours, patientez."); return; }
    setSaving(true); setError('');
    try {
      const res = await api.post('products', { name, slug, price: priceNum, discountPrice: discountPriceNum, prixAchat: prixAchat.trim() !== '' ? parsePrice(prixAchat) : null, supplierSku: supplierSku.trim() || null, category, subCategory, stock: parseInt(stock) || 0, description, metaDescription, image: imageUrl, imageExtension, bestSeller, bonneAffaire, masquer });
      const newId = res.data?.id ?? res.data?.data?.id;
      for (const v of variants) {
        await api.post('variants', { productId: newId, name: v.name, price: getEffectivePrice(v), stock: v.stock, sku: v.sku, option1Value: v.option1Value, option2Value: v.option2Value, option3Value: v.option3Value, image: '', imageExtension: '' });
      }
      navigate(`/produits/${newId}`);
    } catch (err: any) { setError('Erreur : ' + (err.response?.data?.message || err.message)); }
    finally { setSaving(false); }
  };

  return (
    <>
      {error && <div className={styles.error}>{error}</div>}

      {/* BOUTON SAUVEGARDER */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
        <button onClick={handleCreate} disabled={saving || uploading} className={styles.btnSave}>
          {saving ? 'Création...' : uploading ? 'Upload...' : 'Créer le produit'}
        </button>
      </div>

      {/* RECHERCHE FOURNISSEUR */}
      <div className={fStyles.fournisseurPanel}>
        <div className={fStyles.fournisseurHeader} onClick={() => setShowFournisseur((v) => !v)}>
          <span className={fStyles.fournisseurTitle}>🔍 Rechercher chez le fournisseur</span>
          <span className={fStyles.fournisseurSite}>grossisteecigarette.fr</span>
          <span className={fStyles.fournisseurToggle}>{showFournisseur ? '▲' : '▼'}</span>
        </div>
        {showFournisseur && (
          <div className={fStyles.fournisseurBody}>
            <div className={fStyles.fournisseurSearchRow}>
              <input type="text" placeholder="Nom du produit..." value={fournisseurQuery}
                onChange={(e) => setFournisseurQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleFournisseurSearch(); }}
                className={fStyles.fournisseurInput} autoFocus />
              <button onClick={handleFournisseurSearch} disabled={fournisseurLoading || fournisseurQuery.trim().length < 2} className={fStyles.fournisseurBtn}>
                {fournisseurLoading ? '⏳...' : '🔍 Rechercher'}
              </button>
            </div>
            {fournisseurError && <div className={fStyles.fournisseurError}>{fournisseurError}</div>}
            {fournisseurResults.length > 0 && (
              <>
                <div className={fStyles.fournisseurCount}>{fournisseurResults.length} résultat(s) — cliquez pour importer</div>
                <div className={fStyles.fournisseurGrid}>
                  {fournisseurResults.map((p, i) => (
                    <div key={i} className={fStyles.fournisseurCard} onClick={() => handleImportFournisseur(p)}>
                      <div className={fStyles.fournisseurCardImg}>
                        {p.image ? <img src={p.image} alt={p.name} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : <span className={fStyles.fournisseurCardImgPlaceholder}>📷</span>}
                      </div>
                      <div className={fStyles.fournisseurCardInfo}>
                        <div className={fStyles.fournisseurCardName}>{p.name}</div>
                        {p.description && <div className={fStyles.fournisseurCardBrand}>{p.description.substring(0, 60)}{p.description.length > 60 ? '…' : ''}</div>}
                        {p.price && <div className={fStyles.fournisseurCardPrice}>{p.price}</div>}
                      </div>
                      <div className={fStyles.fournisseurCardImport}>⬇ Importer</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* INFOS */}
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>Informations générales</h2>
        <div className={styles.grid}>
          <div className={styles.field}><label className={styles.label}>Nom *</label>
            <input type="text" value={name} onChange={(e) => handleNameChange(e.target.value)} className={styles.input} placeholder="Ex: Pantera 100ml" autoFocus /></div>
          <div className={styles.field}><label className={styles.label}>Slug</label>
            <input type="text" value={slug} onChange={(e) => setSlug(e.target.value)} className={styles.input} /></div>
          <div className={styles.field}><label className={styles.label}>Catégorie</label>
            <select value={category} onChange={(e) => { setCategory(e.target.value); setSubCategory(''); }} className={styles.input}>
              <option value="">— Choisir —</option>
              {categoryList.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select></div>
          <div className={styles.field}><label className={styles.label}>Sous-catégorie</label>
            <select value={subCategory} onChange={(e) => setSubCategory(e.target.value)} className={styles.input} disabled={subCategoryList.length === 0}>
              <option value="">— Choisir —</option>
              {subCategoryList.map((s) => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div className={styles.field}><label className={styles.label}>Prix (€)</label>
            <input type="text" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} className={styles.input} placeholder="0.00" /></div>
          <div className={styles.field}><label className={styles.label}>Prix remisé (€)</label>
            <input type="text" inputMode="decimal" value={discountPrice} onChange={(e) => setDiscountPrice(e.target.value)} className={styles.input} placeholder="Laisser vide" /></div>
          <div className={styles.field}><label className={styles.label}>Stock</label>
            <input type="number" value={stock} onChange={(e) => setStock(e.target.value)} className={styles.input} min={0} /></div>
          <div className={styles.field}><label className={styles.label}>Prix d'achat (€)</label>
            <input type="text" inputMode="decimal" value={prixAchat} onChange={(e) => setPrixAchat(e.target.value)} className={styles.input} placeholder="Ce que vous payez" /></div>
          <div className={styles.field}><label className={styles.label}>Code produit fournisseur</label>
            <input type="text" value={supplierSku} onChange={(e) => setSupplierSku(e.target.value)} className={styles.input} placeholder="Ex: ACCGLASSZEUSDUAL55" /></div>
        </div>
        <div className={styles.fieldFull}><label className={styles.label}>Description</label>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} className={styles.textarea} rows={4} /></div>
        <div className={styles.fieldFull}><label className={styles.label}>Meta description</label>
          <textarea value={metaDescription} onChange={(e) => setMetaDescription(e.target.value)} className={styles.textarea} rows={2} /></div>
        <div className={styles.checkboxRow}>
          <label className={styles.checkboxLabel}><input type="checkbox" checked={bestSeller} onChange={(e) => setBestSeller(e.target.checked)} /> Best Seller</label>
          <label className={styles.checkboxLabel}><input type="checkbox" checked={bonneAffaire} onChange={(e) => setBonneAffaire(e.target.checked)} /> Bonne Affaire</label>
          <label className={styles.checkboxLabel}><input type="checkbox" checked={masquer} onChange={(e) => setMasquer(e.target.checked)} /> Masquer</label>
        </div>
      </div>

      {/* IMAGE */}
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>Image du produit</h2>
        <div className={styles.imageSection}>
          <div className={styles.imagePreviewWrap}>
            {previewUrl || imageUrl ? <img src={previewUrl || imageUrl} alt="" className={styles.imagePreview} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : <span className={styles.imagePreviewPlaceholder}>📷</span>}
          </div>
          <div className={styles.imageControls}>
            <div>
              <label className={styles.label} style={{ marginBottom: '0.4rem', display: 'block' }}>Uploader via FTP</label>
              <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif" onChange={handleFileSelect} className={styles.filePicker} id="file-picker-manuel" disabled={uploading} />
              <label htmlFor="file-picker-manuel" className={styles.btnPickFile} style={{ opacity: uploading ? 0.6 : 1 }}>
                {uploading ? '⏳ Upload...' : '📂 Choisir (upload auto)'}
              </label>
            </div>
            {uploadStatus && <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}><span className={styles.uploadStatus}>{uploadStatus}</span><button onClick={handleDeleteImage} className={styles.btnDeleteImage}>✕</button></div>}
            {uploadError && <span className={styles.uploadError}>{uploadError}</span>}
            <div className={styles.imageUrlRow}>
              <label className={styles.label}>Ou URL manuelle</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input type="text" value={imageUrl} onChange={(e) => { setImageUrl(e.target.value); setImageExtension(e.target.value.split('.').pop()?.toLowerCase() ?? ''); setPreviewUrl(''); setUploadStatus(''); }} className={styles.input} placeholder="https://..." />
                {imageUrl && <button onClick={handleDeleteImage} className={styles.btnDeleteImage}>✕</button>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* VARIANTES */}
      <div className={styles.card}>
        <div className={styles.variantsHeader}>
          <h2 className={styles.cardTitle}>Variantes ({variants.length})</h2>
          {!addingVariant && <button onClick={() => { const v = emptyVariant(tempIdCounter++); v.price = priceNum > 0 ? String(priceNum) : '0'; setNewVariant(v); setAddingVariant(true); }} className={styles.btnAdd}>+ Ajouter</button>}
        </div>
        {(variants.length > 0 || addingVariant) && (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th>Nom</th><th>Prix (€)</th><th>Stock</th><th>SKU</th><th>Opt 1</th><th>Opt 2</th><th>Opt 3</th><th></th></tr></thead>
              <tbody>
                {variants.map((v) => {
                  const eff = getEffectivePrice(v);
                  return (
                    <tr key={v._tempId}>
                      <td><input type="text" value={v.name} onChange={(e) => setVariants(p => p.map(x => x._tempId === v._tempId ? { ...x, name: e.target.value } : x))} className={styles.cellInput} /></td>
                      <td style={{ minWidth: 160 }}>
                        <input type="text" inputMode="decimal" value={v.priceSyncMode === 'manual' ? v.price : eff} disabled={v.priceSyncMode !== 'manual'} onChange={(e) => setVariants(p => p.map(x => x._tempId === v._tempId ? { ...x, price: e.target.value } : x))} className={styles.cellInputSm} style={v.priceSyncMode !== 'manual' ? { textDecoration: 'line-through', opacity: 0.5 } : {}} />
                        <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.2rem', flexWrap: 'wrap' }}>
                          <label style={{ fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '0.2rem', cursor: 'pointer' }}><input type="radio" checked={v.priceSyncMode === 'product'} onChange={() => setVariants(p => p.map(x => x._tempId === v._tempId ? { ...x, priceSyncMode: 'product' } : x))} />={priceNum}€</label>
                          {discountPriceNum !== null && <label style={{ fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '0.2rem', cursor: 'pointer' }}><input type="radio" checked={v.priceSyncMode === 'discount'} onChange={() => setVariants(p => p.map(x => x._tempId === v._tempId ? { ...x, priceSyncMode: 'discount' } : x))} />={discountPriceNum}€ remisé</label>}
                          <label style={{ fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '0.2rem', cursor: 'pointer' }}><input type="radio" checked={v.priceSyncMode === 'manual'} onChange={() => setVariants(p => p.map(x => x._tempId === v._tempId ? { ...x, priceSyncMode: 'manual' } : x))} />Manuel</label>
                        </div>
                      </td>
                      <td><input type="number" value={v.stock} onChange={(e) => setVariants(p => p.map(x => x._tempId === v._tempId ? { ...x, stock: parseInt(e.target.value) || 0 } : x))} className={styles.cellInputSm} /></td>
                      <td><input type="text" value={v.sku} onChange={(e) => setVariants(p => p.map(x => x._tempId === v._tempId ? { ...x, sku: e.target.value } : x))} className={styles.cellInputSm} /></td>
                      <td><input type="text" value={v.option1Value} onChange={(e) => setVariants(p => p.map(x => x._tempId === v._tempId ? { ...x, option1Value: e.target.value } : x))} className={styles.cellInputSm} /></td>
                      <td><input type="text" value={v.option2Value} onChange={(e) => setVariants(p => p.map(x => x._tempId === v._tempId ? { ...x, option2Value: e.target.value } : x))} className={styles.cellInputSm} /></td>
                      <td><input type="text" value={v.option3Value} onChange={(e) => setVariants(p => p.map(x => x._tempId === v._tempId ? { ...x, option3Value: e.target.value } : x))} className={styles.cellInputSm} /></td>
                      <td><button onClick={() => setVariants(p => p.filter(x => x._tempId !== v._tempId))} className={styles.btnDelete}>×</button></td>
                    </tr>
                  );
                })}
                {addingVariant && newVariant && (
                  <tr className={styles.newRow}>
                    <td><input type="text" placeholder="Nom *" value={newVariant.name} onChange={(e) => setNewVariant({ ...newVariant, name: e.target.value })} className={styles.cellInput} autoFocus /></td>
                    <td style={{ minWidth: 160 }}>
                      <input type="text" inputMode="decimal" value={newVariant.priceSyncMode === 'manual' ? newVariant.price : (newVariant.priceSyncMode === 'product' ? priceNum : discountPriceNum ?? priceNum)} disabled={newVariant.priceSyncMode !== 'manual'} onChange={(e) => setNewVariant({ ...newVariant, price: e.target.value })} className={styles.cellInputSm} style={newVariant.priceSyncMode !== 'manual' ? { textDecoration: 'line-through', opacity: 0.5 } : {}} />
                      <div style={{ display: 'flex', gap: '0.25rem', marginTop: '0.2rem', flexWrap: 'wrap' }}>
                        <label style={{ fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '0.2rem', cursor: 'pointer' }}><input type="radio" checked={newVariant.priceSyncMode === 'product'} onChange={() => setNewVariant({ ...newVariant, priceSyncMode: 'product' })} />={priceNum}€</label>
                        {discountPriceNum !== null && <label style={{ fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '0.2rem', cursor: 'pointer' }}><input type="radio" checked={newVariant.priceSyncMode === 'discount'} onChange={() => setNewVariant({ ...newVariant, priceSyncMode: 'discount' })} />={discountPriceNum}€</label>}
                        <label style={{ fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '0.2rem', cursor: 'pointer' }}><input type="radio" checked={newVariant.priceSyncMode === 'manual'} onChange={() => setNewVariant({ ...newVariant, priceSyncMode: 'manual' })} />Manuel</label>
                      </div>
                    </td>
                    <td><input type="number" placeholder="0" value={newVariant.stock} onChange={(e) => setNewVariant({ ...newVariant, stock: parseInt(e.target.value) || 0 })} className={styles.cellInputSm} /></td>
                    <td><input type="text" placeholder="SKU" value={newVariant.sku} onChange={(e) => setNewVariant({ ...newVariant, sku: e.target.value })} className={styles.cellInputSm} /></td>
                    <td colSpan={3}><input type="text" placeholder="Option 1" value={newVariant.option1Value} onChange={(e) => setNewVariant({ ...newVariant, option1Value: e.target.value })} className={styles.cellInputSm} /></td>
                    <td className={styles.newRowActions}>
                      <button onClick={() => { if (!newVariant.name.trim()) { alert('Nom requis'); return; } setVariants(p => [...p, newVariant]); setAddingVariant(false); setNewVariant(null); }} className={styles.btnConfirm}>✓</button>
                      <button onClick={() => { setAddingVariant(false); setNewVariant(null); }} className={styles.btnCancel}>×</button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {variants.length === 0 && !addingVariant && <p className={styles.noData}>Aucune variante.</p>}
      </div>
    </>
  );
};

// ─── MODE AUTO ────────────────────────────────────────────────────────────────
const ModeAuto: React.FC<{ navigate: ReturnType<typeof useNavigate> }> = ({ navigate }) => {
  const categoryList = useMemo(() => getCategories(), []);

  // Paramètres communs
  const [category, setCategory] = useState('');
  const [subCategory, setSubCategory] = useState('');
  const [price, setPrice] = useState('');
  const [discountPrice, setDiscountPrice] = useState('');
  const [prixAchat, setPrixAchat] = useState('');
  const [stock, setStock] = useState('0');
  const [bestSeller, setBestSeller] = useState(false);
  const [bonneAffaire, setBonneAffaire] = useState(false);
  const [masquer, setMasquer] = useState(false);

  // Recherche fournisseur
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FournisseurProduct[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Produits sélectionnés
  const [selected, setSelected] = useState<SelectedProduct[]>([]);

  // Création
  const [creating, setCreating] = useState(false);
  const [logs, setLogs] = useState<{ name: string; status: 'pending' | 'ok' | 'error'; msg?: string }[]>([]);
  const [done, setDone] = useState(false);

  const subCategoryList = useMemo(
    () => categoryList.find((c) => c.name === category)?.subCategories ?? [],
    [categoryList, category]
  );
  const priceNum = parsePrice(price);
  const discountPriceNum = discountPrice.trim() !== '' ? parsePrice(discountPrice) : null;

  const handleSearch = async () => {
    if (query.trim().length < 2) return;
    setSearching(true); setSearchError(''); setResults([]);
    try {
      const res = await api.get(`fournisseur/search?q=${encodeURIComponent(query.trim())}`);
      setResults(res.data.data ?? []);
      if ((res.data.data ?? []).length === 0) setSearchError('Aucun produit trouvé.');
    } catch (err: any) { setSearchError('Erreur : ' + (err.response?.data?.message || err.message)); }
    finally { setSearching(false); }
  };

  const toggleSelect = (p: FournisseurProduct) => {
    const already = selected.find((s) => s.fournisseur.name === p.name);
    if (already) {
      setSelected((prev) => prev.filter((s) => s.fournisseur.name !== p.name));
    } else {
      const autoSlug = toSlug(p.name);
      // Auto-catégorisation si pas encore défini
      const autoResult = autoCategorize(p.name);
      if (!category && autoResult) { setCategory(autoResult.category); setSubCategory(autoResult.subCategory); }
      setSelected((prev) => [...prev, { _key: selectedKeyCounter++, fournisseur: p, name: p.name, slug: autoSlug }]);
    }
  };

  const removeSelected = (key: number) => setSelected((prev) => prev.filter((s) => s._key !== key));

  const handleCreate = async () => {
    if (selected.length === 0) { alert('Sélectionnez au moins un produit.'); return; }
    setCreating(true);
    setDone(false);
    setLogs(selected.map((s) => ({ name: s.name, status: 'pending' })));

    for (let i = 0; i < selected.length; i++) {
      const s = selected[i];
      const imgUrl = s.fournisseur.imageLarge || s.fournisseur.image;
      const ext = imgUrl.split('.').pop()?.toLowerCase().split('?')[0] ?? '';
      const finalCat = category || (autoCategorize(s.name)?.category ?? '');
      const finalSub = subCategory || (autoCategorize(s.name)?.subCategory ?? '');

      // Récupérer description + tableau depuis la page détail fournisseur
      let description = s.fournisseur.description || '';
      if (s.fournisseur.url) {
        try {
          const detailRes = await api.get(`fournisseur/detail?url=${encodeURIComponent(s.fournisseur.url)}`);
          if (detailRes.data?.description) description = detailRes.data.description;
        } catch { /* garder la description de la liste */ }
      }

      try {
        await api.post('products', {
          name: s.name,
          slug: s.slug,
          price: priceNum,
          discountPrice: discountPriceNum,
          prixAchat: prixAchat.trim() !== '' ? parsePrice(prixAchat) : (parsePrice(s.fournisseur.price) || null),
          category: finalCat,
          subCategory: finalSub,
          stock: parseInt(stock) || 0,
          description,
          metaDescription: '',
          image: imgUrl,
          imageExtension: ext,
          bestSeller,
          bonneAffaire,
          masquer,
        });
        setLogs((prev) => prev.map((l, idx) => idx === i ? { ...l, status: 'ok' } : l));
      } catch (err: any) {
        const msg = err.response?.data?.message || err.message;
        setLogs((prev) => prev.map((l, idx) => idx === i ? { ...l, status: 'error', msg } : l));
      }
    }
    setCreating(false);
    setDone(true);
  };

  return (
    <>
      {/* PARAMÈTRES COMMUNS */}
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>Paramètres communs à tous les produits</h2>
        <div className={styles.grid}>
          <div className={styles.field}><label className={styles.label}>Catégorie</label>
            <select value={category} onChange={(e) => { setCategory(e.target.value); setSubCategory(''); }} className={styles.input}>
              <option value="">— Auto selon le nom —</option>
              {categoryList.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select></div>
          <div className={styles.field}><label className={styles.label}>Sous-catégorie</label>
            <select value={subCategory} onChange={(e) => setSubCategory(e.target.value)} className={styles.input} disabled={subCategoryList.length === 0}>
              <option value="">— Auto selon le nom —</option>
              {subCategoryList.map((s) => <option key={s} value={s}>{s}</option>)}
            </select></div>
          <div className={styles.field}><label className={styles.label}>Prix de vente (€)</label>
            <input type="text" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} className={styles.input} placeholder="0.00 — laisser vide pour 0" /></div>
          <div className={styles.field}><label className={styles.label}>Prix remisé (€)</label>
            <input type="text" inputMode="decimal" value={discountPrice} onChange={(e) => setDiscountPrice(e.target.value)} className={styles.input} placeholder="Laisser vide" /></div>
          <div className={styles.field}><label className={styles.label}>Stock initial</label>
            <input type="number" value={stock} onChange={(e) => setStock(e.target.value)} className={styles.input} min={0} /></div>
          <div className={styles.field}><label className={styles.label}>Prix d'achat (€)</label>
            <input type="text" inputMode="decimal" value={prixAchat} onChange={(e) => setPrixAchat(e.target.value)} className={styles.input} placeholder="Ce que vous payez" /></div>
        </div>
        <div className={styles.checkboxRow}>
          <label className={styles.checkboxLabel}><input type="checkbox" checked={bestSeller} onChange={(e) => setBestSeller(e.target.checked)} /> Best Seller</label>
          <label className={styles.checkboxLabel}><input type="checkbox" checked={bonneAffaire} onChange={(e) => setBonneAffaire(e.target.checked)} /> Bonne Affaire</label>
          <label className={styles.checkboxLabel}><input type="checkbox" checked={masquer} onChange={(e) => setMasquer(e.target.checked)} /> Masquer</label>
        </div>
      </div>

      {/* RECHERCHE FOURNISSEUR */}
      <div className={fStyles.fournisseurPanel}>
        <div className={fStyles.fournisseurHeader} style={{ cursor: 'default' }}>
          <span className={fStyles.fournisseurTitle}>🔍 Rechercher et sélectionner des produits</span>
          <span className={fStyles.fournisseurSite}>grossisteecigarette.fr</span>
        </div>
        <div className={fStyles.fournisseurBody}>
          <div className={fStyles.fournisseurSearchRow}>
            <input type="text" placeholder="Nom, marque, type de produit..." value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
              className={fStyles.fournisseurInput} />
            <button onClick={handleSearch} disabled={searching || query.trim().length < 2} className={fStyles.fournisseurBtn}>
              {searching ? '⏳...' : '🔍 Rechercher'}
            </button>
          </div>
          {searchError && <div className={fStyles.fournisseurError}>{searchError}</div>}
          {results.length > 0 && (
            <>
              <div className={fStyles.fournisseurCount}>{results.length} résultat(s) — cliquez pour sélectionner (✓ = sélectionné)</div>
              <div className={fStyles.fournisseurGrid}>
                {results.map((p, i) => {
                  const isSelected = selected.some((s) => s.fournisseur.name === p.name);
                  return (
                    <div key={i} className={`${fStyles.fournisseurCard} ${isSelected ? fStyles.fournisseurCardSelected : ''}`} onClick={() => toggleSelect(p)}>
                      <div className={fStyles.fournisseurCardImg}>
                        {isSelected && <div className={fStyles.fournisseurCardCheck}>✓</div>}
                        {p.image ? <img src={p.image} alt={p.name} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} /> : <span className={fStyles.fournisseurCardImgPlaceholder}>📷</span>}
                      </div>
                      <div className={fStyles.fournisseurCardInfo}>
                        <div className={fStyles.fournisseurCardName}>{p.name}</div>
                        {p.description && <div className={fStyles.fournisseurCardBrand}>{p.description.substring(0, 50)}{p.description.length > 50 ? '…' : ''}</div>}
                        {p.price && <div className={fStyles.fournisseurCardPrice}>{p.price}</div>}
                      </div>
                      <div className={fStyles.fournisseurCardImport} style={{ background: isSelected ? '#27ae60' : undefined }}>
                        {isSelected ? '✓ Sélectionné' : '+ Sélectionner'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* PANIER DE SÉLECTION */}
      {selected.length > 0 && (
        <div className={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h2 className={styles.cardTitle} style={{ margin: 0 }}>Produits à créer ({selected.length})</h2>
            <button onClick={handleCreate} disabled={creating} className={styles.btnSave} style={{ fontSize: '1rem', padding: '0.7rem 1.5rem' }}>
              {creating ? `Création en cours...` : `⚡ Créer ${selected.length} produit${selected.length > 1 ? 's' : ''}`}
            </button>
          </div>
          <div className={fStyles.selectedList}>
            {selected.map((s) => (
              <div key={s._key} className={fStyles.selectedItem}>
                {(s.fournisseur.image) && <img src={s.fournisseur.image} alt="" className={fStyles.selectedThumb} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />}
                <div className={fStyles.selectedInfo}>
                  <input type="text" value={s.name} onChange={(e) => setSelected(prev => prev.map(x => x._key === s._key ? { ...x, name: e.target.value, slug: toSlug(e.target.value) } : x))} className={fStyles.selectedNameInput} />
                  <span className={fStyles.selectedSlug}>{s.slug}</span>
                </div>
                <button onClick={() => removeSelected(s._key)} className={styles.btnDelete}>×</button>
              </div>
            ))}
          </div>

          {/* Logs de création */}
          {logs.length > 0 && (
            <div className={fStyles.logsPanel}>
              {logs.map((l, i) => (
                <div key={i} className={`${fStyles.logLine} ${l.status === 'ok' ? fStyles.logOk : l.status === 'error' ? fStyles.logError : fStyles.logPending}`}>
                  {l.status === 'pending' ? '⏳' : l.status === 'ok' ? '✅' : '❌'} {l.name} {l.msg ? `— ${l.msg}` : ''}
                </div>
              ))}
              {done && (
                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem' }}>
                  <button onClick={() => navigate('/produits')} className={styles.btnSave}>Voir les produits</button>
                  <button onClick={() => { setLogs([]); setDone(false); setSelected([]); }} className={styles.btnBack}>Recommencer</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

// ─── COMPOSANT PRINCIPAL ──────────────────────────────────────────────────────
export const ProduitCreate: React.FC = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'manuel' | 'auto'>('manuel');

  return (
    <Layout>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>

        {/* EN-TÊTE avec sélecteur de mode */}
        <div className={styles.header}>
          <button onClick={() => navigate('/produits')} className={styles.btnBack}>← Retour</button>
          <h1 className={styles.title}>Nouveau produit</h1>
          <div className={fStyles.modeSelector}>
            <button
              className={`${fStyles.modeBtn} ${mode === 'manuel' ? fStyles.modeBtnActive : ''}`}
              onClick={() => setMode('manuel')}
            >
              ✏️ Manuel
            </button>
            <button
              className={`${fStyles.modeBtn} ${mode === 'auto' ? fStyles.modeBtnActive : ''}`}
              onClick={() => setMode('auto')}
            >
              ⚡ Automatique
            </button>
          </div>
        </div>

        {mode === 'manuel' ? <ModeManuel navigate={navigate} /> : <ModeAuto navigate={navigate} />}
      </div>
    </Layout>
  );
};
